import React, { useState, useEffect, useCallback, memo } from 'react';
import { supabase } from '../../lib/supabase';

/* ─── CONSTANTS ─────────────────────────────────────────────────────────────── */
const emptyItinerary = {
    destination: '', region: '', tagline: '', duration: '', price_from: '',
    hotel: '', meals: '', best_time: '', overview: '',
    overview_accommodation: [], overview_sightseeing: '', overview_transport: [], overview_meals: [],
    highlights: [], images: [], inclusions: [], exclusions: [],
    days: [], slug: '', active: true,
};

const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const stripOverviewFields = (payload) => {
    const { overview_accommodation, overview_sightseeing, overview_transport, overview_meals, ...rest } = payload;
    return rest;
};

const parseSightseeingText = (raw = '') => {
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
    const sections = [];
    let current = null;

    lines.forEach(line => {
        if (line.startsWith('-') || line.startsWith('•')) {
            if (!current) {
                current = { title: 'Sightseeing', items: [] };
                sections.push(current);
            }
            current.items.push(line.replace(/^[-•]\s*/, '').trim());
            return;
        }

        current = { title: line, items: [] };
        sections.push(current);
    });

    return sections;
};

const serializeSightseeingSections = (sections = []) => sections
    .filter(section => section.title || (section.items || []).length > 0)
    .map(section => {
        const title = (section.title || 'Sightseeing').trim();
        const bullets = (section.items || []).map(item => `- ${item}`).join('\n');
        return bullets ? `${title}\n${bullets}` : title;
    })
    .join('\n\n');

/* ─── LIST FIELD ─────────────────────────────────────────────────────────────
   Defined outside any component so its identity is stable across renders.
   memo() prevents re-render unless props actually changed.
────────────────────────────────────────────────────────────────────────────── */
const ListField = memo(({ items, field, icon, placeholder, newVal, setNew, onUpdate, onRemove, onAdd }) => (
    <div className="admin-list-field">
        {(items || []).map((v, i) => (
            <div className="admin-list-item" key={i}>
                <i className={icon} style={{ fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }} />
                <input value={v} onChange={e => onUpdate(field, i, e.target.value)} />
                <button className="admin-btn-icon danger" onClick={() => onRemove(field, i)}>
                    <i className="fa-solid fa-xmark" />
                </button>
            </div>
        ))}
        <div className="admin-list-add">
            <input
                placeholder={placeholder}
                value={newVal}
                onChange={e => setNew(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onAdd(field, newVal, setNew)}
            />
            <button className="admin-add-btn" onClick={() => onAdd(field, newVal, setNew)}>+ Add</button>
        </div>
    </div>
));

/* ─── EDIT MODAL ─────────────────────────────────────────────────────────────
   Completely isolated component with its OWN local form state.
   Typing here only re-renders this component, NOT the parent list.
────────────────────────────────────────────────────────────────────────────── */
const EditModal = memo(({ initialForm, editId, regions, onSave, onClose }) => {
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const [newHighlight, setNewHighlight] = useState('');
    const [newImage, setNewImage] = useState('');
    const [newInclusion, setNewInclusion] = useState('');
    const [newExclusion, setNewExclusion] = useState('');
    const [newAccommodation, setNewAccommodation] = useState('');
    const [newTransport, setNewTransport] = useState('');
    const [newMeal, setNewMeal] = useState('');
    const [newActivity, setNewActivity] = useState({});
    const [sightseeingSections, setSightseeingSections] = useState(parseSightseeingText(initialForm.overview_sightseeing || ''));
    const [newSightTitle, setNewSightTitle] = useState('');
    const [newSightItem, setNewSightItem] = useState({});

    /* Stable field setters */
    const setF = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);

    const handleListUpdate = useCallback((field, index, value) => {
        setForm(f => {
            const arr = [...(f[field] || [])];
            arr[index] = value;
            return { ...f, [field]: arr };
        });
    }, []);

    const handleListRemove = useCallback((field, index) => {
        setForm(f => ({ ...f, [field]: (f[field] || []).filter((_, i) => i !== index) }));
    }, []);

    const handleListAdd = useCallback((field, val, setter) => {
        if (!val.trim()) return;
        setForm(f => ({ ...f, [field]: [...(f[field] || []), val.trim()] }));
        setter('');
    }, []);

    const addDay = useCallback(() => {
        setForm(f => ({
            ...f,
            days: [
                ...f.days,
                {
                    day: f.days.length + 1,
                    title: '',
                    icon: '📍',
                    activities: [],
                    description: '',
                    included_meals: '',
                    optional_note: '',
                    notes: '',
                }
            ]
        }));
    }, []);

    const updateDay = useCallback((i, k, v) => {
        setForm(f => {
            const days = [...f.days];
            days[i] = { ...days[i], [k]: v };
            return { ...f, days };
        });
    }, []);

    const removeDay = useCallback((i) => {
        setForm(f => ({ ...f, days: f.days.filter((_, idx) => idx !== i).map((d, idx) => ({ ...d, day: idx + 1 })) }));
    }, []);

    const addActivity = useCallback((di) => {
        setNewActivity(p => {
            const act = (p[di] || '').trim();
            if (!act) return p;
            setForm(f => {
                const days = [...f.days];
                days[di] = { ...days[di], activities: [...(days[di].activities || []), act] };
                return { ...f, days };
            });
            return { ...p, [di]: '' };
        });
    }, []);

    const updateActivity = useCallback((di, ai, value) => {
        setForm(f => {
            const days = [...f.days];
            const activities = [...(days[di].activities || [])];
            activities[ai] = value;
            days[di] = { ...days[di], activities };
            return { ...f, days };
        });
    }, []);

    const removeActivity = useCallback((di, ai) => {
        setForm(f => {
            const days = [...f.days];
            days[di] = { ...days[di], activities: days[di].activities.filter((_, i) => i !== ai) };
            return { ...f, days };
        });
    }, []);

    const addSightSection = useCallback(() => {
        const title = newSightTitle.trim();
        if (!title) return;
        setSightseeingSections(prev => [...prev, { title, items: [] }]);
        setNewSightTitle('');
    }, [newSightTitle]);

    const updateSightSectionTitle = useCallback((si, title) => {
        setSightseeingSections(prev => prev.map((s, i) => i === si ? { ...s, title } : s));
    }, []);

    const removeSightSection = useCallback((si) => {
        setSightseeingSections(prev => prev.filter((_, i) => i !== si));
    }, []);

    const addSightItem = useCallback((si) => {
        const val = (newSightItem[si] || '').trim();
        if (!val) return;
        setSightseeingSections(prev => prev.map((s, i) => i === si ? { ...s, items: [...(s.items || []), val] } : s));
        setNewSightItem(prev => ({ ...prev, [si]: '' }));
    }, [newSightItem]);

    const updateSightItem = useCallback((si, ii, value) => {
        setSightseeingSections(prev => prev.map((s, i) => {
            if (i !== si) return s;
            const items = [...(s.items || [])];
            items[ii] = value;
            return { ...s, items };
        }));
    }, []);

    const removeSightItem = useCallback((si, ii) => {
        setSightseeingSections(prev => prev.map((s, i) => i === si ? { ...s, items: (s.items || []).filter((_, idx) => idx !== ii) } : s));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        const payload = {
            ...form,
            overview_sightseeing: serializeSightseeingSections(sightseeingSections),
            slug: form.slug || autoSlug(form.destination)
        };
        await onSave(payload, editId);
        setSaving(false);
    };

    return (
        <div className="admin-modal-overlay" onClick={onClose}>
            <div className="admin-modal admin-modal-lg" onClick={e => e.stopPropagation()}>
                <div className="admin-modal-header">
                    <h3><i className="fa-solid fa-map-location-dot" /> {editId ? `Edit — ${form.destination}` : 'Add New Destination'}</h3>
                    <button className="admin-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="admin-modal-body">
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 12 }}>Basic Info</p>
                    <div className="admin-form-grid" style={{ marginBottom: 20 }}>
                        <div className="admin-field">
                            <label className="admin-label">Destination *</label>
                            <input className="admin-input" value={form.destination}
                                onChange={e => { setF('destination', e.target.value); if (!editId) setF('slug', autoSlug(e.target.value)); }}
                                placeholder="e.g. Goa" />
                        </div>

                        <div className="admin-field">
                            <label className="admin-label">
                                Region *
                                {regions.length === 0 && <span style={{ color: 'var(--gold)', fontWeight: 400, marginLeft: 6 }}>(Add regions in Holidays Menu tab first)</span>}
                            </label>
                            {regions.length > 0 ? (
                                <select className="admin-select" value={form.region} onChange={e => setF('region', e.target.value)}>
                                    <option value="">— Select Region —</option>
                                    {regions.map(r => <option key={r.region} value={r.region}>{r.region}</option>)}
                                </select>
                            ) : (
                                <input className="admin-input" value={form.region} onChange={e => setF('region', e.target.value)} placeholder="Type region manually..." />
                            )}
                        </div>

                        <div className="admin-field">
                            <label className="admin-label">Tagline</label>
                            <input className="admin-input" value={form.tagline} onChange={e => setF('tagline', e.target.value)} placeholder="e.g. Sun, Sand & Spice" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Duration</label>
                            <input className="admin-input" value={form.duration} onChange={e => setF('duration', e.target.value)} placeholder="5 Days / 4 Nights" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Price From</label>
                            <input className="admin-input" value={form.price_from} onChange={e => setF('price_from', e.target.value)} placeholder="₹12,000" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Hotel</label>
                            <input className="admin-input" value={form.hotel} onChange={e => setF('hotel', e.target.value)} placeholder="3★ / 4★ Hotel" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Meals</label>
                            <input className="admin-input" value={form.meals} onChange={e => setF('meals', e.target.value)} placeholder="Daily Breakfast" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Best Time</label>
                            <input className="admin-input" value={form.best_time} onChange={e => setF('best_time', e.target.value)} placeholder="Nov – Feb" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">URL Slug</label>
                            <input className="admin-input" value={form.slug} onChange={e => setF('slug', e.target.value)} placeholder="goa" />
                        </div>
                        <div className="admin-field">
                            <div className="admin-toggle-row">
                                <span className="admin-toggle-label">Active (visible on site)</span>
                                <input className="admin-toggle" type="checkbox" checked={!!form.active} onChange={e => setF('active', e.target.checked)} />
                            </div>
                        </div>
                        <div className="admin-field admin-form-full">
                            <label className="admin-label">Overview</label>
                            <textarea className="admin-textarea" rows={4} value={form.overview} onChange={e => setF('overview', e.target.value)} placeholder="Detailed description..." />
                        </div>
                    </div>

                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>Highlights</p>
                    <div style={{ marginBottom: 20 }}>
                        <ListField items={form.highlights} field="highlights" icon="fa-solid fa-star"
                            placeholder="Add highlight..." newVal={newHighlight} setNew={setNewHighlight}
                            onUpdate={handleListUpdate} onRemove={handleListRemove} onAdd={handleListAdd} />
                    </div>

                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>Image URLs</p>
                    <div style={{ marginBottom: 20 }}>
                        <ListField items={form.images} field="images" icon="fa-solid fa-image"
                            placeholder="Paste image URL..." newVal={newImage} setNew={setNewImage}
                            onUpdate={handleListUpdate} onRemove={handleListRemove} onAdd={handleListAdd} />
                    </div>

                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>Trip Overview Layout (Itinerary Details Page)</p>
                    <div className="admin-form-grid" style={{ marginBottom: 20 }}>
                        <div className="admin-field">
                            <label className="admin-label">Accommodation (bullet items)</label>
                            <ListField items={form.overview_accommodation} field="overview_accommodation" icon="fa-solid fa-hotel"
                                placeholder="Add accommodation line..." newVal={newAccommodation} setNew={setNewAccommodation}
                                onUpdate={handleListUpdate} onRemove={handleListRemove} onAdd={handleListAdd} />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Flight and Transport (bullet items)</label>
                            <ListField items={form.overview_transport} field="overview_transport" icon="fa-solid fa-plane-departure"
                                placeholder="Add transport line..." newVal={newTransport} setNew={setNewTransport}
                                onUpdate={handleListUpdate} onRemove={handleListRemove} onAdd={handleListAdd} />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Meals (bullet items)</label>
                            <ListField items={form.overview_meals} field="overview_meals" icon="fa-solid fa-utensils"
                                placeholder="Add meal line..." newVal={newMeal} setNew={setNewMeal}
                                onUpdate={handleListUpdate} onRemove={handleListRemove} onAdd={handleListAdd} />
                        </div>
                        <div className="admin-field admin-form-full">
                            <label className="admin-label">Sightseeing (Add / Edit / Delete by section)</label>

                            {(sightseeingSections || []).map((section, si) => (
                                <div key={si} className="admin-day-card" style={{ marginBottom: 10 }}>
                                    <div className="admin-day-header">
                                        <input
                                            className="admin-day-title-input"
                                            placeholder="Section title (e.g., Thimphu, Bhutan)"
                                            value={section.title || ''}
                                            onChange={e => updateSightSectionTitle(si, e.target.value)}
                                        />
                                        <button className="admin-btn-icon danger" onClick={() => removeSightSection(si)}>
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                    <div className="admin-day-body">
                                        <div className="admin-list-field">
                                            {(section.items || []).map((item, ii) => (
                                                <div className="admin-list-item" key={ii}>
                                                    <i className="fa-solid fa-location-dot" style={{ fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }} />
                                                    <input value={item} onChange={e => updateSightItem(si, ii, e.target.value)} />
                                                    <button className="admin-btn-icon danger" onClick={() => removeSightItem(si, ii)}>
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="admin-list-add">
                                                <input
                                                    placeholder="Add sightseeing point..."
                                                    value={newSightItem[si] || ''}
                                                    onChange={e => setNewSightItem(prev => ({ ...prev, [si]: e.target.value }))}
                                                    onKeyDown={e => e.key === 'Enter' && addSightItem(si)}
                                                />
                                                <button className="admin-add-btn" onClick={() => addSightItem(si)}>+ Add</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="admin-list-add">
                                <input
                                    placeholder="New sightseeing section title..."
                                    value={newSightTitle}
                                    onChange={e => setNewSightTitle(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addSightSection()}
                                />
                                <button className="admin-add-btn" onClick={addSightSection}>+ Add Section</button>
                            </div>
                        </div>
                    </div>

                    <div className="admin-form-grid" style={{ marginBottom: 20 }}>
                        <div className="admin-field">
                            <label className="admin-label">Inclusions</label>
                            <ListField items={form.inclusions} field="inclusions" icon="fa-solid fa-check"
                                placeholder="Add inclusion..." newVal={newInclusion} setNew={setNewInclusion}
                                onUpdate={handleListUpdate} onRemove={handleListRemove} onAdd={handleListAdd} />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Exclusions</label>
                            <ListField items={form.exclusions} field="exclusions" icon="fa-solid fa-xmark"
                                placeholder="Add exclusion..." newVal={newExclusion} setNew={setNewExclusion}
                                onUpdate={handleListUpdate} onRemove={handleListRemove} onAdd={handleListAdd} />
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase' }}>Day-by-Day Plan ({form.days.length} days)</p>
                        <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={addDay}><i className="fa-solid fa-plus" /> Add Day</button>
                    </div>

                    {form.days.map((day, di) => (
                        <div className="admin-day-card" key={di}>
                            <div className="admin-day-header">
                                <div className="admin-day-num">{day.day}</div>
                                <input className="admin-day-icon-input" value={day.icon} onChange={e => updateDay(di, 'icon', e.target.value)} title="Emoji" />
                                <input className="admin-day-title-input" placeholder="Day title..." value={day.title} onChange={e => updateDay(di, 'title', e.target.value)} />
                                <button className="admin-btn-icon danger" onClick={() => removeDay(di)}><i className="fa-solid fa-trash" /></button>
                            </div>
                            <div className="admin-day-body">
                                <div className="admin-field" style={{ marginBottom: 10 }}>
                                    <label className="admin-label">Detailed Description</label>
                                    <textarea
                                        className="admin-textarea"
                                        rows={4}
                                        value={day.description || ''}
                                        onChange={e => updateDay(di, 'description', e.target.value)}
                                        placeholder="Write full day details like transfer, sightseeing flow, overnight stay..."
                                    />
                                </div>

                                <div className="admin-form-grid" style={{ marginBottom: 10 }}>
                                    <div className="admin-field">
                                        <label className="admin-label">Included Meals</label>
                                        <input
                                            className="admin-input"
                                            value={day.included_meals || ''}
                                            onChange={e => updateDay(di, 'included_meals', e.target.value)}
                                            placeholder="Breakfast, Lunch, Dinner"
                                        />
                                    </div>
                                    <div className="admin-field">
                                        <label className="admin-label">Optional</label>
                                        <input
                                            className="admin-input"
                                            value={day.optional_note || ''}
                                            onChange={e => updateDay(di, 'optional_note', e.target.value)}
                                            placeholder="Optional activities text"
                                        />
                                    </div>
                                </div>

                                <div className="admin-field" style={{ marginBottom: 10 }}>
                                    <label className="admin-label">Notes</label>
                                    <input
                                        className="admin-input"
                                        value={day.notes || ''}
                                        onChange={e => updateDay(di, 'notes', e.target.value)}
                                        placeholder="Special notes for travellers"
                                    />
                                </div>

                                <div className="admin-list-field">
                                    {(day.activities || []).map((act, ai) => (
                                        <div className="admin-list-item" key={ai}>
                                            <i className="fa-solid fa-circle-dot" style={{ color: 'var(--text-3)', fontSize: 9, flexShrink: 0 }} />
                                            <input value={act} onChange={e => updateActivity(di, ai, e.target.value)} />
                                            <button className="admin-btn-icon danger" onClick={() => removeActivity(di, ai)}><i className="fa-solid fa-xmark" /></button>
                                        </div>
                                    ))}
                                    <div className="admin-list-add">
                                        <input placeholder="Add activity..." value={newActivity[di] || ''}
                                            onChange={e => setNewActivity(p => ({ ...p, [di]: e.target.value }))}
                                            onKeyDown={e => e.key === 'Enter' && addActivity(di)} />
                                        <button className="admin-add-btn" onClick={() => addActivity(di)}>+ Add</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="admin-modal-footer">
                    <button className="admin-btn admin-btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <><i className="fa-solid fa-circle-notch fa-spin" /> Saving...</> : <><i className="fa-solid fa-floppy-disk" /> Save Itinerary</>}
                    </button>
                </div>
            </div>
        </div>
    );
});

/* ─── MAIN TAB ───────────────────────────────────────────────────────────────
   Only manages the list view + filters. Modal state is self-contained above.
────────────────────────────────────────────────────────────────────────────── */
const ItineraryTab = ({ showToast }) => {
    const [items, setItems] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // null | { initialForm, editId }
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [search, setSearch] = useState('');
    const [filterRegion, setFilterRegion] = useState('all');

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        setLoading(true);
        const [{ data: itins }, { data: navRegions }] = await Promise.all([
            supabase.from('itineraries').select('*').order('region').order('destination'),
            supabase.from('navbar_config').select('region, color, fa_icon').order('display_order'),
        ]);
        setItems(itins || []);
        setRegions(navRegions || []);
        setLoading(false);
    };

    const fetchItems = async () => {
        const { data } = await supabase.from('itineraries').select('*').order('region').order('destination');
        setItems(data || []);
    };

    const openAdd = () => {
        const firstRegion = regions.length > 0 ? regions[0].region : '';
        setModal({ initialForm: { ...emptyItinerary, region: firstRegion }, editId: null });
    };

    const openEdit = (item) => {
        setModal({
            initialForm: {
                ...item,
                days: item.days || [], highlights: item.highlights || [],
                images: item.images || [], inclusions: item.inclusions || [],
                exclusions: item.exclusions || [],
                overview_accommodation: item.overview_accommodation || [],
                overview_sightseeing: item.overview_sightseeing || '',
                overview_transport: item.overview_transport || [],
                overview_meals: item.overview_meals || [],
            },
            editId: item.id,
        });
    };

    /* Called by EditModal when Save button is pressed */
    const handleSave = async (payload, editId) => {
        if (!payload.destination) { showToast('Destination name is required', 'error'); return; }
        if (!payload.region) { showToast('Please select a region', 'error'); return; }
        const runSave = (dataToSave) => (
            editId
                ? supabase.from('itineraries').update(dataToSave).eq('id', editId)
                : supabase.from('itineraries').insert([dataToSave])
        );

        let { error } = await runSave(payload);

        const missingOverviewColumns = (error?.message || '').toLowerCase().includes('column')
            && (error?.message || '').toLowerCase().includes('overview_');

        if (missingOverviewColumns) {
            ({ error } = await runSave(stripOverviewFields(payload)));
            if (!error) {
                showToast('Saved basic itinerary. Run scripts/alter-itineraries-trip-overview.sql to enable Trip Overview fields.', 'warning');
                fetchItems();
                setModal(null);
                return;
            }
        }

        if (!error) {
            showToast(editId ? 'Itinerary updated!' : 'Itinerary added!', 'success');
            fetchItems();
            setModal(null);
            return;
        }

        showToast('Error: ' + error.message, 'error');
    };

    const deleteItem = async (id) => {
        const { error } = await supabase.from('itineraries').delete().eq('id', id);
        if (!error) { showToast('Deleted!', 'success'); fetchItems(); setDeleteConfirm(null); }
    };

    const toggleActive = async (item) => {
        await supabase.from('itineraries').update({ active: !item.active }).eq('id', item.id);
        fetchItems();
    };

    const filtered = items.filter(i => {
        const matchSearch = !search || i.destination.toLowerCase().includes(search.toLowerCase());
        const matchRegion = filterRegion === 'all' || i.region === filterRegion;
        return matchSearch && matchRegion;
    });

    const grouped = regions.reduce((acc, r) => {
        const dests = filtered.filter(i => i.region === r.region);
        if (dests.length > 0) acc.push({ ...r, destinations: dests });
        return acc;
    }, []);
    const unassigned = filtered.filter(i => !regions.find(r => r.region === i.region));
    if (unassigned.length > 0) grouped.push({ region: 'Other', color: '#64748b', fa_icon: 'fa-solid fa-globe', destinations: unassigned });

    return (
        <div className="admin-tab-content">
            {/* Stats */}
            <div className="admin-stats-row">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon gold"><i className="fa-solid fa-map" /></div>
                    <div><div className="admin-stat-val">{items.length}</div><div className="admin-stat-lbl">Total Destinations</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon blue"><i className="fa-solid fa-layer-group" /></div>
                    <div><div className="admin-stat-val">{regions.length}</div><div className="admin-stat-lbl">Regions</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon green"><i className="fa-solid fa-eye" /></div>
                    <div><div className="admin-stat-val">{items.filter(i => i.active).length}</div><div className="admin-stat-lbl">Active</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon red"><i className="fa-solid fa-eye-slash" /></div>
                    <div><div className="admin-stat-val">{items.filter(i => !i.active).length}</div><div className="admin-stat-lbl">Hidden</div></div>
                </div>
            </div>

            {/* Header */}
            <div className="admin-section-header">
                <div className="admin-section-title"><i className="fa-solid fa-map-location-dot" /> Itineraries by Region</div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <div className="admin-search-wrap">
                        <i className="fa-solid fa-search" />
                        <input className="admin-search" placeholder="Search destination..." value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <button className="admin-btn admin-btn-primary" onClick={openAdd}>
                        <i className="fa-solid fa-plus" /> Add Destination
                    </button>
                </div>
            </div>

            {/* Region filter tabs */}
            {regions.length > 0 && (
                <div className="admin-filter-bar" style={{ marginBottom: 16 }}>
                    <button className={`admin-filter-btn ${filterRegion === 'all' ? 'active' : ''}`} onClick={() => setFilterRegion('all')}>
                        All ({items.length})
                    </button>
                    {regions.map(r => {
                        const count = items.filter(i => i.region === r.region).length;
                        return count > 0 ? (
                            <button key={r.region} className={`admin-filter-btn ${filterRegion === r.region ? 'active' : ''}`}
                                onClick={() => setFilterRegion(r.region)}
                                style={{ borderColor: filterRegion === r.region ? r.color : undefined, color: filterRegion === r.region ? r.color : undefined }}>
                                <i className={r.fa_icon} style={{ marginRight: 5 }} />
                                {r.region} <span style={{ fontWeight: 700 }}>({count})</span>
                            </button>
                        ) : null;
                    })}
                </div>
            )}

            {regions.length === 0 && !loading && (
                <div style={{ padding: '12px 16px', background: 'var(--gold-dim)', border: '1px solid rgba(217,119,6,0.2)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--gold)' }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }} />
                    No regions found. Go to <strong>Holidays Menu</strong> tab first and add your regions.
                </div>
            )}

            {/* Grouped List */}
            {loading ? (
                <div className="admin-loading"><div className="admin-spinner" /><p>Loading…</p></div>
            ) : filtered.length === 0 ? (
                <div className="admin-empty"><i className="fa-solid fa-map" /><p>No destinations yet. Click "Add Destination" to start.</p></div>
            ) : (
                grouped.map(group => (
                    <div key={group.region} style={{ marginBottom: 24 }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                            background: `${group.color}18`, borderLeft: `3px solid ${group.color}`,
                            borderRadius: '0 8px 8px 0', marginBottom: 8,
                        }}>
                            <i className={group.fa_icon} style={{ color: group.color, fontSize: 13 }} />
                            <span style={{ fontWeight: 700, fontSize: 13, color: group.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{group.region}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 4 }}>— {group.destinations.length} destination{group.destinations.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="admin-itin-list">
                            {group.destinations.map(item => (
                                <div className="admin-itin-row" key={item.id} style={{ borderLeft: `2px solid ${group.color}44`, marginLeft: 8 }}>
                                    <div className="admin-itin-flag-placeholder" style={{ background: group.color }} />
                                    <div className="admin-itin-name">{item.destination}</div>
                                    <div className="admin-itin-dur">
                                        <i className="fa-solid fa-calendar" style={{ marginRight: 4, color: 'var(--text-3)' }} />
                                        {item.duration || '—'}
                                    </div>
                                    <div className="admin-itin-price">{item.price_from || '—'}</div>
                                    <span className={`badge ${item.active ? 'badge-active' : 'badge-inactive'}`}>
                                        {item.active ? 'Active' : 'Hidden'}
                                    </span>
                                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{(item.days || []).length} days</div>
                                    <div className="admin-itin-actions">
                                        <button className="admin-btn-icon" onClick={() => toggleActive(item)} title="Toggle visibility">
                                            <i className={`fa-solid ${item.active ? 'fa-eye-slash' : 'fa-eye'}`} />
                                        </button>
                                        <button className="admin-btn-icon" onClick={() => openEdit(item)} title="Edit">
                                            <i className="fa-solid fa-pen" />
                                        </button>
                                        <button className="admin-btn-icon danger" onClick={() => setDeleteConfirm(item.id)} title="Delete">
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="admin-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3><i className="fa-solid fa-triangle-exclamation" /> Confirm Delete</h3>
                            <button className="admin-modal-close" onClick={() => setDeleteConfirm(null)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="admin-modal-body"><p style={{ color: 'var(--text-2)', fontSize: 14 }}>This will permanently delete the itinerary. Cannot be undone.</p></div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button className="admin-btn admin-btn-danger" onClick={() => deleteItem(deleteConfirm)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit / Add Modal — fully isolated, handles its own state */}
            {modal && (
                <EditModal
                    initialForm={modal.initialForm}
                    editId={modal.editId}
                    regions={regions}
                    onSave={handleSave}
                    onClose={() => setModal(null)}
                />
            )}
        </div>
    );
};

export default ItineraryTab;
